import {
  Button,
  Flex,
  Heading,
  Icon,
  ListItem,
  Spinner,
  Stack,
  StackDivider,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { useMemo } from 'react';
import { HiExclamationCircle } from 'react-icons/hi2';

import { TrpcOutputs } from '../config/trpc';
import { Updates } from '../screens/Import';
import { SummaryItem } from './Rates/ErrorCard';

type Error = { line?: number; messages: string[] };

interface ErrorSummaryProps {
  errors: Error[];
}

export const ErrorSummary = ({ errors = [] }: ErrorSummaryProps) => {
  // Group by line number
  const groupedErrors = errors.reduce(
    (acc, error) => {
      const line = error?.line || 0;
      acc[line] = acc[line] || { line, messages: [] };
      acc[line].messages.push(...error.messages);
      return acc;
    },
    {} as Record<number, Error>,
  );

  return (
    <Stack divider={<StackDivider />} flex={1} overflowY="auto" p={4}>
      <SummaryItem
        title={`${errors?.length} errors found on ${Object.entries(groupedErrors).length} rows. Please fix the errors in the sheet and refresh`}
        icon={<Icon as={HiExclamationCircle} color="red.500" />}
      />
      {Object.entries(groupedErrors)
        ?.slice(0, 10)
        ?.map(([line, error]) => (
          <SummaryItem
            key={line}
            title={line ? `Row #${line}` : 'General'}
            description={
              <UnorderedList>
                {error?.messages?.map((message) => <ListItem key={message}>{message}</ListItem>)}
              </UnorderedList>
            }
          />
        ))}
      <Show if={errors?.length > 10}>
        <SummaryItem title={`(${errors?.length - 10} more error(s) omitted)`} />
      </Show>
    </Stack>
  );
};

interface TableInfoSummaryProps {
  tableInfo: TrpcOutputs['getTableInfo'];
}

export const TableInfoSummary = ({ tableInfo }: TableInfoSummaryProps) => {
  if (!tableInfo) return <Stack flex={1} />;

  return (
    <Stack divider={<StackDivider />} flex={1} overflowY="auto" p={4}>
      <SummaryItem title={`${tableInfo?.rowCount || 'No'} rows found`} />
      <SummaryItem title={`${tableInfo?.rateCount || 'No'} rates will be created`} />
      <SummaryItem title={`Target airline: ${tableInfo?.provider || 'Unknown'} `} />
      <SummaryItem
        key="lane"
        title="The following lanes will be created:"
        description={tableInfo.newLanes.map((lane) => (
          <Flex key={lane.lane}>
            Line #{lane.line} - {lane.lane}
          </Flex>
        ))}
      ></SummaryItem>
    </Stack>
  );
};

interface UpdatesProgressProps {
  updates: Updates;
  logs: string[];
  isFinishedImporting: boolean;
  handleReset: () => void;
}

export const UpdatesProgress = ({ updates, logs, isFinishedImporting, handleReset }: UpdatesProgressProps) => {
  return (
    <Flex direction="column">
      <Flex fontWeight="bold" gap="5">
        <Flex width="15em">
          Routes: {updates.creates.count ?? '???'} / {updates.creates.total ?? '???'}
        </Flex>
        <Flex width="15em">
          Segments: {updates.segments.count ?? '???'} / {updates.segments.total ?? '???'}
        </Flex>
        <Flex width="15em">
          Rates: {updates.upserts.count ?? '???'} / {updates.upserts.total ?? '???'}
        </Flex>
      </Flex>
      <Flex direction="column-reverse" flexGrow="1">
        {...logs.map((log, i) => (
          <Text key={i} opacity={Math.max(2, 11 - (logs.length - i)) / 10}>
            {log}
          </Text>
        ))}
        <Show if={isFinishedImporting}>
          <Flex justifyContent="center" py="2">
            <Button colorScheme="green" onClick={handleReset} size="md" w="full">
              New Import
            </Button>
          </Flex>
        </Show>
      </Flex>
    </Flex>
  );
};

type ImportPendingProps = {
  isImporting: boolean;
  isFetchingSheet: boolean;
  isValidating: boolean;
  tableInfo: TrpcOutputs['getTableInfo'];
  updates: Updates;
  logs: string[];
  handleReset: () => void;
  isFinishedImporting: boolean;
};

export const ImportPending = ({
  handleReset,
  isImporting = false,
  isFetchingSheet = false,
  isValidating = false,
  isFinishedImporting,
  tableInfo,
  updates,
  logs,
}: ImportPendingProps) => {
  const loadingCopy = useMemo(() => {
    if (isImporting) return 'Importing data...';
    if (isFetchingSheet) return 'Fetching sheet data...';
    if (isValidating) return 'Validating sheet data...';
    return '';
  }, [isImporting, isFetchingSheet, isValidating]);

  return (
    <Stack flex={1}>
      <Stack p={4} borderBottomWidth={1}>
        <Heading size="md">Summary</Heading>
      </Stack>
      <Show if={isFetchingSheet || isValidating}>
        <Stack p={4} align="center" justify="center">
          <Spinner />
          <Text>{loadingCopy}</Text>
        </Stack>
      </Show>
      {isImporting || isFinishedImporting ? (
        <Stack align="center" justify="center" w="full">
          <UpdatesProgress
            updates={updates}
            logs={logs}
            isFinishedImporting={isFinishedImporting}
            handleReset={handleReset}
          />
        </Stack>
      ) : tableInfo?.errors?.length ? (
        <ErrorSummary errors={tableInfo.errors} />
      ) : (
        <TableInfoSummary tableInfo={tableInfo} />
      )}
    </Stack>
  );
};
